// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useAdminOrgScope } from "@/hooks/useAdminOrgScope";

interface Venue {
  id: string;
  name: string;
  city?: string;
  state?: string;
}

interface VenueFormData {
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  phone: string;
  website: string;
  logo_url: string;
}

const VenueManagement = () => {
  const { scopeOrgId, createOrgId, ready } = useAdminOrgScope();
  const [formData, setFormData] = useState<VenueFormData>({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    website: '',
    logo_url: ''
  });

  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [venueToDelete, setVenueToDelete] = useState<Venue | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!ready) return;
    fetchVenues();
  }, [ready, scopeOrgId]);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      // Venues are a shared, platform-wide master library — every org admin and
      // the super admin sees the same full list (no organization filtering).
      const { data: venuesData, error: venuesError } = await supabase
        .from('venues')
        .select('id, name, city, state')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (venuesError) throw venuesError;

      setVenues(venuesData || []);
    } catch (error) {
      console.error('Error fetching venues:', error);
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVenue = async (venue: Venue) => {
    // Fetch full venue details
    const { data, error } = await supabase
      .from('venues')
      .select('*')
      .eq('id', venue.id)
      .single();

    if (error) {
      toast.error('Failed to load venue details');
      return;
    }

    setFormData({
      name: data.name || '',
      address: data.address || '',
      city: data.city || '',
      state: data.state || '',
      zip_code: data.zip_code || '',
      phone: data.phone || '',
      website: data.website || '',
      logo_url: data.logo_url || ''
    });
    setEditingVenue(venue);
  };

  const handleDeleteVenue = async () => {
    if (!venueToDelete) return;

    try {
      setDeleting(true);
      
      // Clear all venue references from related tables
      await Promise.all([
        supabase.from('events').update({ main_venue_id: null }).eq('main_venue_id', venueToDelete.id),
        supabase.from('events').update({ venue_id: null }).eq('venue_id', venueToDelete.id),
        supabase.from('event_schedule_entries').update({ venue_id: null }).eq('venue_id', venueToDelete.id),
        supabase.from('tournament_matches').update({ venue_id: null }).eq('venue_id', venueToDelete.id),
      ]);

      // Delete the venue
      const { error } = await supabase
        .from('venues')
        .delete()
        .eq('id', venueToDelete.id);

      if (error) throw error;

      toast.success('Venue deleted successfully');
      setVenueToDelete(null);
      fetchVenues();
    } catch (error: any) {
      console.error('Error deleting venue:', error);
      toast.error('Failed to delete venue');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingVenue(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      phone: '',
      website: '',
      logo_url: ''
    });
  };

  const handleInputChange = (field: keyof VenueFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setUploadingLogo(true);

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `venue-logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('event-venues')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data } = supabase.storage
        .from('event-venues')
        .getPublicUrl(filePath);

      // Update form data with logo URL
      handleInputChange('logo_url', data.publicUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Venue name is required');
      return;
    }

    try {
      setSubmitting(true);
      
      const venueData = {
        name: formData.name.trim(),
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        zip_code: formData.zip_code.trim() || null,
        phone: formData.phone.trim() || null,
        website: formData.website.trim() || null,
        logo_url: formData.logo_url || null
      };

      if (editingVenue) {
        const { error } = await supabase
          .from('venues')
          .update(venueData)
          .eq('id', editingVenue.id);

        if (error) throw error;
        toast.success('Venue updated successfully');
        setEditingVenue(null);
      } else {
        const { error } = await supabase
          .from('venues')
          .insert([{ ...venueData, organization_id: createOrgId }]);

        if (error) throw error;
        toast.success('Venue created successfully');
      }
      
      // Reset form
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        phone: '',
        website: '',
        logo_url: ''
      });

      // Refresh venues list
      fetchVenues();
    } catch (error) {
      console.error('Error creating venue:', error);
      toast.error('Failed to create venue');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{editingVenue ? 'Edit Venue' : 'Add New Venue'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Venue Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter venue name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter address"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  placeholder="Enter state"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  value={formData.zip_code}
                  onChange={(e) => handleInputChange('zip_code', e.target.value)}
                  placeholder="Enter ZIP code"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="Enter website URL"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo">Venue Logo</Label>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="cursor-pointer"
                  />
                </div>
                {uploadingLogo && (
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Uploading...
                  </div>
                )}
              </div>
              {formData.logo_url && (
                <div className="mt-2">
                  <img
                    src={formData.logo_url}
                    alt="Venue logo preview"
                    className="h-16 w-16 object-contain border rounded"
                  />
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Upload an image file (max 5MB). Supported formats: JPG, PNG, GIF, WebP
              </p>
            </div>

            <div className="flex justify-end gap-2">
              {editingVenue && (
                <Button type="button" variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingVenue ? 'Update Venue' : 'Create Venue'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Venues</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading venues...</span>
            </div>
          ) : venues.length === 0 ? (
            <p className="text-muted-foreground py-4">No venues found. Create your first venue above.</p>
          ) : (
            <div className="space-y-2">
              {venues.map((venue) => (
                <div key={venue.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <h4 className="font-medium">{venue.name}</h4>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {(venue.city || venue.state) && (
                        <span>{[venue.city, venue.state].filter(Boolean).join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditVenue(venue)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setVenueToDelete(venue)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!venueToDelete} onOpenChange={() => setVenueToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Venue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{venueToDelete?.name}"? This will also delete all associated courts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteVenue}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VenueManagement;
